/**
 * ManageSubscriptionModal Component
 * 
 * Modal for managing existing subscriptions - view details, change billing cycle,
 * update payment methods, and cancel subscriptions.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Object} props.currentStore - Current store data with subscription info
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Crown, Calendar, CreditCard, AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function ManageSubscriptionModal({ isOpen, onClose, currentUser, currentStore }) {
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [error, setError] = useState(null);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);
  const [cancellationSubmitted, setCancellationSubmitted] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionDetails();
    }
  }, [isOpen]);
  
  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/subscription/details`, {
        headers: {
          'X-User-Email': currentUser?.email,
          'X-User-Role': currentUser?.role
        }
      });
      setSubscriptionDetails(response.data);
    } catch (err) {
      console.error('Failed to fetch subscription details:', err);
      setError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };
  
  const getPlanDisplayName = () => {
    if (!subscriptionDetails) return 'Unknown';
    const tier = subscriptionDetails.tier || 'base';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };
  
  const getBillingCycleDisplay = () => {
    if (!subscriptionDetails?.subscription_type) return 'N/A';
    return subscriptionDetails.subscription_type.charAt(0).toUpperCase() + 
           subscriptionDetails.subscription_type.slice(1);
  };
  
  const handleCancelSubscription = () => {
    setShowCancelForm(true);
  };
  
  const handleSubmitCancellation = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    try {
      setSubmittingCancel(true);
      
      // Send cancellation request to backend
      await axios.post(`${API}/subscription/cancel-request`, {
        store_name: currentStore?.name || 'N/A',
        owner_name: currentUser?.name || 'N/A',
        owner_email: currentUser?.email || 'N/A',
        plan: `${getPlanDisplayName()} ${getBillingCycleDisplay()}`,
        cancellation_reason: cancellationReason
      }, {
        headers: {
          'X-User-Email': currentUser?.email,
          'X-User-Role': currentUser?.role
        }
      });
      
      // Show success confirmation instead of closing
      setCancellationSubmitted(true);
    } catch (err) {
      console.error('Failed to submit cancellation:', err);
      toast.error('Failed to submit cancellation request. Please try again or email admin@caterlog.net directly.');
    } finally {
      setSubmittingCancel(false);
    }
  };
  
  const handleUpgradePlan = () => {
    // Close this modal and open upgrade modal
    onClose();
    toast.info('Choose your plan to upgrade');
    // Trigger upgrade modal opening with source tracking (this will be handled by parent component)
    window.dispatchEvent(new CustomEvent('openUpgradeModal', { detail: { source: 'manageSubscription' } }));
  };
  
  const handleChangeBillingCycle = async () => {
    const newCycle = subscriptionDetails?.subscription_type === 'monthly' ? 'yearly' : 'monthly';
    const currentTier = subscriptionDetails?.tier || 'base';
    
    // Calculate pricing
    const pricing = {
      base_monthly: '$19.99/month',
      base_yearly: '$220/year',
      premium_monthly: '$29.99/month',
      premium_yearly: '$330/year'
    };
    const newPrice = pricing[`${currentTier}_${newCycle}`];
    
    const confirmMessage = `Change billing cycle from ${subscriptionDetails?.subscription_type} to ${newCycle}?\n\nYou will be charged ${newPrice} now. The new billing cycle will take effect at your next billing date (${formatDate(subscriptionDetails?.next_billing_date)}).`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setLoading(true);
      toast.info('Redirecting to payment...');
      
      // Create checkout session for billing cycle change
      const response = await axios.post(`${API}/subscription/change-billing-cycle`, {
        new_cycle: newCycle,
        current_tier: currentTier,
        frontend_url: window.location.origin
      }, {
        headers: {
          'X-User-Email': currentUser?.email,
          'X-User-Role': currentUser?.role
        }
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Failed to change billing cycle:', err);
      toast.error('Failed to process billing cycle change. Please try again or contact admin@caterlog.net');
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Subscription Management</DialogTitle>
          </DialogHeader>
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p>Loading subscription details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Subscription Management</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={onClose} className="btn-bawarchi-primary">
              Close
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  const isFreeTrialOrExpired = subscriptionDetails?.subscription_status === 'free_trial' || 
                                subscriptionDetails?.trial_expired;
  const isActive = subscriptionDetails?.subscription_status === 'active';
  const isPaymentFailed = subscriptionDetails?.subscription_status === 'payment_failed';
  const isBase = subscriptionDetails?.tier === 'base';
  
  // Show cancellation success confirmation
  if (cancellationSubmitted) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {
        setCancellationSubmitted(false);
        setShowCancelForm(false);
        setCancellationReason('');
        onClose();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Cancellation Request Submitted</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
              <div className="text-green-600 mb-4">
                <CheckCircle className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-xl font-bold text-green-800 mb-2">
                Request Successfully Submitted
              </h3>
              <p className="text-green-700">
                Your cancellation request has been received.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-blue-900">What Happens Next:</h4>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Our team will review your request and contact you at <strong>{currentUser?.email}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>Your access will continue until the end of your current billing period: <strong>{formatDate(subscriptionDetails?.next_billing_date)}</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span>You'll receive a confirmation email once the cancellation is processed</span>
                </li>
              </ul>
            </div>
            
            <div className="text-center pt-2">
              <button
                onClick={() => {
                  setCancellationSubmitted(false);
                  setShowCancelForm(false);
                  setCancellationReason('');
                  onClose();
                }}
                className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Show cancellation form if requested
  if (showCancelForm) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {
        setShowCancelForm(false);
        setCancellationReason('');
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Cancel Subscription</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Please note:</strong> Your cancellation request will be reviewed by our team. 
                Your subscription will remain active until the end of your current billing period.
              </p>
            </div>
            
            <div>
              <Label htmlFor="cancellation-reason">Reason for Cancellation *</Label>
              <Textarea
                id="cancellation-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please tell us why you're canceling (e.g., switching to another service, not using features, too expensive, etc.)"
                rows={5}
                className="mt-2"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSubmitCancellation}
                disabled={submittingCancel}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {submittingCancel ? 'Submitting...' : 'Submit Cancellation Request'}
              </button>
              <button
                onClick={() => {
                  setShowCancelForm(false);
                  setCancellationReason('');
                }}
                disabled={submittingCancel}
                className="flex-1 border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Subscription Management</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Current Plan Section */}
          <Card className={`border-2 ${isActive ? 'border-green-500 bg-green-50' : isFreeTrialOrExpired ? 'border-orange-500 bg-orange-50' : 'border-red-500 bg-red-50'}`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold mb-1">
                    {isFreeTrialOrExpired ? 'Free Trial' : getPlanDisplayName()} Plan
                  </h3>
                  <p className="text-sm text-gray-600">
                    {isFreeTrialOrExpired ? 'Trial Period' : 
                     isPaymentFailed ? '⚠️ Payment Failed' :
                     isActive ? '✓ Active' : subscriptionDetails?.subscription_status}
                  </p>
                </div>
                <div className="text-right">
                  {!isFreeTrialOrExpired && (
                    <>
                      <p className="text-2xl font-bold">${subscriptionDetails?.amount}</p>
                      <p className="text-sm text-gray-600">per {getBillingCycleDisplay().toLowerCase()}</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {isFreeTrialOrExpired ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Trial Started</p>
                      <p className="font-medium">{formatDate(subscriptionDetails?.trial_start_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Trial Ends</p>
                      <p className="font-medium">{formatDate(subscriptionDetails?.trial_end_date)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Billing Cycle</p>
                      <p className="font-medium">{getBillingCycleDisplay()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Next Billing Date</p>
                      <p className="font-medium">{formatDate(subscriptionDetails?.next_billing_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Payment Method</p>
                      <p className="font-medium">
                        {subscriptionDetails?.payment_method_last4 
                          ? `${subscriptionDetails.payment_method_brand || 'Card'} ending in ${subscriptionDetails.payment_method_last4}`
                          : 'No payment method on file'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="font-medium">${subscriptionDetails?.amount} / {getBillingCycleDisplay().toLowerCase()}</p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Free Trial - Show Upgrade Buttons */}
            {isFreeTrialOrExpired && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={handleUpgradePlan}
                  className="btn-bawarchi-primary flex items-center justify-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade to Base
                </button>
                <button
                  onClick={handleUpgradePlan}
                  className="btn-bawarchi-primary flex items-center justify-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade to Premium
                </button>
              </div>
            )}
            
            {/* Active Base - Show Upgrade to Premium */}
            {isActive && isBase && (
              <button
                onClick={handleUpgradePlan}
                className="w-full btn-bawarchi-primary flex items-center justify-center gap-2"
              >
                <Crown className="w-4 h-4" />
                Upgrade to Premium
              </button>
            )}
            
            {/* Active Subscription - Show Change Billing Cycle */}
            {isActive && !isFreeTrialOrExpired && (
              <button
                onClick={handleChangeBillingCycle}
                className="w-full border-2 border-orange-600 text-orange-600 px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Change Billing Cycle ({subscriptionDetails?.subscription_type === 'monthly' ? 'Switch to Yearly' : 'Switch to Monthly'})
              </button>
            )}
            
            {/* Payment Failed - Update Payment */}
            {isPaymentFailed && (
              <button
                onClick={() => alert('Please contact admin@caterlog.net to update your payment method.')}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Update Payment Method
              </button>
            )}
            
            {/* Cancel Subscription - Only for Active Paid Users */}
            {isActive && !isFreeTrialOrExpired && (
              <button
                onClick={handleCancelSubscription}
                className="w-full border-2 border-red-600 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Cancel Subscription
              </button>
            )}
          </div>
          
          {/* Help Text */}
          <div className="text-center text-sm text-gray-600 pt-4 border-t">
            Need help? Contact us at{' '}
            <a href="mailto:admin@caterlog.net" className="text-orange-600 hover:underline">
              admin@caterlog.net
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Subscription Success Page Component - Must be defined before App component

export default ManageSubscriptionModal;
